<template>
  <div class="payer-report-details-page">
    <PageHeader
      title="Payer Report Details"
      :breadcrumbs="[
        { label: 'Financial Assistant', to: { name: 'chatbot' }, icon: 'las la-home' },
        { label: candidateName, to: { name: 'chatbot-candidate', params: { candidateId } } },
        {
          label: 'Automated Assessment',
          to: { name: 'chatbot-automated-assessment-input', params: { candidateId } }
        },
        { label: 'Payer Report Details' }
      ]"
    />

    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading payer report context...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="las la-exclamation-triangle"></i>
        </div>
        <h3>Error Loading Payer Report</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button type="button" class="retry-btn" @click="loadPayerReportContext">
            <i class="las la-redo"></i>
            Try Again
          </button>
          <BaseButton
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.4rem 0.875rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }"
            @click="goToAutomatedAssessment"
          >
            <i class="las la-arrow-left"></i>
            Automated Assessment
          </BaseButton>
        </div>
      </div>

      <div v-else class="payer-report-details-content">
        <div class="report-back-action">
          <BaseButton
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.4rem 0.875rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }"
            @click="goToAutomatedAssessment"
          >
            <i class="las la-arrow-left"></i>
            Automated Assessment
          </BaseButton>
        </div>

        <div class="report-overview-card">
          <div class="report-header">
            <div class="report-info">
              <h2 class="report-title">Payer Score Report</h2>
              <div class="report-meta">
                <span v-if="candidateName">{{ candidateName }}</span>
                <span class="report-date">{{ formatDate(new Date()) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="report-editor-wrapper"
          tabindex="0"
          @keydown.left="goToPrevPreviewPage"
          @keydown.right="goToNextPreviewPage"
        >
          <div
            class="report-editor-layout"
            :class="{ 'is-full-width-stack': usesFullWidthEditor }"
          >
            <div
              v-if="!usesFullWidthEditor"
              ref="reportInputsPanel"
              class="report-inputs-panel"
            >
              <h3 class="panel-title">Report Content</h3>
              <p class="panel-page-label">
                Page {{ currentPreviewPage }} — {{ currentPreviewPageTitle }}
              </p>

              <div v-if="currentPreviewPage === 1" class="report-page-inputs">
              <div class="form-section">
                <label class="section-title">Merchant Name</label>
                <p class="field-hint">Shown on page 1 — Cover</p>
                <input
                  v-model="merchantName"
                  type="text"
                  class="text-input"
                  placeholder="Enter merchant name..."
                />
              </div>
            </div>

            <div v-else-if="currentPreviewPage === 2" class="report-page-inputs">
              <div class="form-section">
                <label class="section-title">Company Requirement</label>
                <p class="field-hint">Shown on page 2 — Executive Summary</p>
                <textarea
                  v-model="companyRequirement"
                  class="textarea-input"
                  placeholder="Enter company requirement..."
                  rows="6"
                ></textarea>
              </div>
              <div class="form-section">
                <label class="section-title">Key Authorised Signatory</label>
                <p class="field-hint">Shown on page 2 — general info table</p>
                <input
                  v-model="keyAuthorisedSignatory"
                  type="text"
                  class="text-input"
                  placeholder="Enter key authorised signatory..."
                />
              </div>
            </div>

            <div v-else-if="currentPreviewPage === 3" class="report-page-inputs">
              <AnalysisCard
                title="Prime Payers Overview"
                :items="primePayersOverview"
                card-class="prime-payers-overview-card"
                :always-show="true"
                @update="updatePrimePayersOverview"
              />
              <div class="form-section payer-stats-form-section">
                <label class="section-title">Key Assessment Metrics — Prime Payers</label>
                <table class="payer-stats-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Recent Value (last 12 months)</th>
                      <th>Lifetime Value (last 8 years)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in payerStatRows"
                      :key="`prime-stat-${index}`"
                    >
                      <td class="label-cell">{{ row.label }}</td>
                      <td>
                        <input
                          :value="getPayerStatValue(row.recentKey)"
                          type="text"
                          class="payer-stat-cell-input"
                          :placeholder="row.recentPlaceholder"
                          @input="updatePayerStat(row.recentKey, $event.target.value)"
                        />
                      </td>
                      <td>
                        <input
                          :value="getPayerStatValue(row.lifetimeKey)"
                          type="text"
                          class="payer-stat-cell-input"
                          :placeholder="row.lifetimePlaceholder"
                          @input="updatePayerStat(row.lifetimeKey, $event.target.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="form-section">
                <label class="section-title">Prime Payer PCR Footnote</label>
                <p class="field-hint field-hint-inline">Optional; supports **bold**</p>
                <textarea
                  v-model="primePayerPcrFootnote"
                  class="textarea-input"
                  placeholder="Enter PCR footnote..."
                  rows="3"
                ></textarea>
              </div>
              <div class="form-section payer-stats-form-section">
                <label class="section-title">Customers by Industry Type</label>
                <table class="payer-stats-table industry-input-table">
                  <thead>
                    <tr>
                      <th>Industry Type</th>
                      <th>AR Outstanding (AED)</th>
                      <th>Sales Contribution (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in primePayerSectorStats"
                      :key="`industry-input-${index}`"
                    >
                      <td>
                        <textarea
                          v-model="row.industryType"
                          class="payer-stat-cell-input industry-type-textarea"
                          rows="1"
                          placeholder="Industry type..."
                          @input="onIndustryTypeInput"
                        ></textarea>
                      </td>
                      <td>
                        <input
                          v-model="row.arOutstanding"
                          type="text"
                          class="payer-stat-cell-input"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          v-model="row.salesContribution"
                          type="text"
                          class="payer-stat-cell-input"
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button
                  type="button"
                  class="add-industry-row-btn"
                  @click="addPrimePayerSectorRow"
                >
                  <i class="las la-plus"></i>
                  Add row
                </button>
              </div>
            </div>

            <div v-else-if="currentPreviewPage === 7" class="report-page-inputs">
              <AnalysisCard
                title="Approved Payers Overview"
                :items="approvedPayersOverview"
                card-class="approved-payers-overview-card"
                :always-show="true"
                @update="updateApprovedPayersOverview"
              />
              <div class="form-section payer-stats-form-section">
                <label class="section-title">Key Assessment Metrics — Approved Payers</label>
                <table class="payer-stats-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Recent Value (last 12 months)</th>
                      <th>Lifetime Value (last 8 years)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in payerStatRows"
                      :key="`approved-stat-${index}`"
                    >
                      <td class="label-cell">{{ row.label }}</td>
                      <td>
                        <input
                          :value="getApprovedPayerStatValue(row.recentKey)"
                          type="text"
                          class="payer-stat-cell-input"
                          :placeholder="row.recentPlaceholder"
                          @input="updateApprovedPayerStat(row.recentKey, $event.target.value)"
                        />
                      </td>
                      <td>
                        <input
                          :value="getApprovedPayerStatValue(row.lifetimeKey)"
                          type="text"
                          class="payer-stat-cell-input"
                          :placeholder="row.lifetimePlaceholder"
                          @input="updateApprovedPayerStat(row.lifetimeKey, $event.target.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="form-section">
                <label class="section-title">Approved Payer PCR Footnote</label>
                <p class="field-hint field-hint-inline">Optional; supports **bold**</p>
                <textarea
                  v-model="approvedPayerPcrFootnote"
                  class="textarea-input"
                  placeholder="Enter PCR footnote..."
                  rows="3"
                ></textarea>
              </div>
              <div class="form-section payer-stats-form-section">
                <label class="section-title">Customers by Industry Type</label>
                <table class="payer-stats-table industry-input-table">
                  <thead>
                    <tr>
                      <th>Industry Type</th>
                      <th>AR Outstanding (AED)</th>
                      <th>Sales Contribution (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in approvedPayerSectorStats"
                      :key="`approved-industry-input-${index}`"
                    >
                      <td>
                        <textarea
                          v-model="row.industryType"
                          class="payer-stat-cell-input industry-type-textarea"
                          rows="1"
                          placeholder="Industry type..."
                          @input="onIndustryTypeInput"
                        ></textarea>
                      </td>
                      <td>
                        <input
                          v-model="row.arOutstanding"
                          type="text"
                          class="payer-stat-cell-input"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          v-model="row.salesContribution"
                          type="text"
                          class="payer-stat-cell-input"
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button
                  type="button"
                  class="add-industry-row-btn"
                  @click="addApprovedPayerSectorRow"
                >
                  <i class="las la-plus"></i>
                  Add row
                </button>
              </div>
            </div>

            <div v-else-if="currentPreviewPage === 11" class="report-page-inputs">
              <AnalysisCard
                title="Conclusion"
                :items="conclusionPoints"
                card-class="conclusion-card"
                :always-show="true"
                @update="updateConclusionPoints"
              />
              <AnalysisCard
                title="Recommendations for Eligible Payers Selection"
                :items="recommendationPoints"
                card-class="recommendation-points-card"
                :always-show="true"
                @update="updateRecommendationPoints"
              />
            </div>

            <p v-else class="report-inputs-empty">
              No editable fields on this page. Use the arrows to continue.
            </p>
            </div>

            <div
              class="report-preview-column"
              :class="{ 'report-full-width-preview': usesFullWidthEditor }"
            >
              <div class="report-page-navigator">
                <button
                  type="button"
                  class="report-page-nav-btn"
                  :disabled="currentPreviewPage <= 1"
                  aria-label="Previous page"
                  @click="goToPrevPreviewPage"
                >
                  <i class="las la-arrow-left"></i>
                </button>
                <div class="report-page-nav-meta">
                  <span class="report-page-nav-count">
                    Page {{ currentPreviewPage }} / {{ totalPreviewPages }}
                  </span>
                  <span class="report-page-nav-title">{{ currentPreviewPageTitle }}</span>
                </div>
                <button
                  type="button"
                  class="report-page-nav-btn"
                  :disabled="currentPreviewPage >= totalPreviewPages"
                  aria-label="Next page"
                  @click="goToNextPreviewPage"
                >
                  <i class="las la-arrow-right"></i>
                </button>
              </div>

              <div class="report-preview-panel">
                <PayerScoreReportPreview
                  ref="reportPreview"
                  embedded
                  single-page-view
                  :visible-page="currentPreviewPage"
                  :data="reportPreviewData"
                />
              </div>
            </div>

            <div
              v-if="usesFullWidthEditor"
              class="report-full-width-inputs"
            >
              <h3 class="panel-title">Report Content</h3>
              <p class="panel-page-label">
                Page {{ currentPreviewPage }} — {{ currentPreviewPageTitle }}
              </p>

              <div v-if="currentPreviewPage === 4" class="report-page-inputs">
                <ReportTableEditor
                  v-model="primePayersOutstandingStats"
                  :columns="primePayerOutstandingTableColumns"
                  :empty-row="emptyPrimePayerOutstandingRow"
                />
              </div>

              <div v-else-if="currentPreviewPage === 5" class="report-page-inputs">
                <ReportTableEditor
                  v-model="primePayersSalesStats"
                  :columns="primePayerSalesTableColumns"
                  :empty-row="emptyPrimePayerSalesRow"
                />
              </div>

              <div v-else-if="currentPreviewPage === 6" class="report-page-inputs">
                <ReportTableEditor
                  v-model="primePayersAgingStats"
                  :columns="primePayerAgingTableColumns"
                  :empty-row="emptyPrimePayerAgingRow"
                />
              </div>

              <div v-else-if="currentPreviewPage === 8" class="report-page-inputs">
                <ReportTableEditor
                  v-model="approvedPayersOutstandingStats"
                  :columns="approvedPayerOutstandingTableColumns"
                  :empty-row="emptyApprovedPayerOutstandingRow"
                />
              </div>

              <div v-else-if="currentPreviewPage === 9" class="report-page-inputs">
                <ReportTableEditor
                  v-model="approvedPayersSalesStats"
                  :columns="approvedPayerSalesTableColumns"
                  :empty-row="emptyApprovedPayerSalesRow"
                />
              </div>

              <div v-else-if="currentPreviewPage === 10" class="report-page-inputs">
                <ReportTableEditor
                  v-model="approvedPayersAgingStats"
                  :columns="approvedPayerAgingTableColumns"
                  :empty-row="emptyApprovedPayerAgingRow"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import PageHeader from './components/PageHeader.vue';
import AnalysisCard from './components/creditReport/AnalysisCard.vue';
import PayerScoreReportPreview from './components/PayerScoreReportPreview.vue';
import ReportTableEditor from './components/payerReport/ReportTableEditor.vue';
import {
  PAYER_REPORT_PREVIEW_PAGES,
  PAYER_REPORT_PREVIEW_PAGE_COUNT,
  PAYER_REPORT_FULL_WIDTH_TABLE_PAGES
} from './constants/payerReportEditorPages.js';
import {
  EMPTY_PRIME_PAYER_OUTSTANDING_ROW,
  PRIME_PAYER_OUTSTANDING_TABLE_COLUMNS,
  EMPTY_PRIME_PAYER_SALES_ROW,
  PRIME_PAYER_SALES_TABLE_COLUMNS,
  EMPTY_PRIME_PAYER_AGING_ROW,
  PRIME_PAYER_AGING_TABLE_COLUMNS,
  EMPTY_APPROVED_PAYER_OUTSTANDING_ROW,
  APPROVED_PAYER_OUTSTANDING_TABLE_COLUMNS,
  EMPTY_APPROVED_PAYER_SALES_ROW,
  APPROVED_PAYER_SALES_TABLE_COLUMNS,
  EMPTY_APPROVED_PAYER_AGING_ROW,
  APPROVED_PAYER_AGING_TABLE_COLUMNS
} from './constants/payerReportTableConfigs.js';

const PAYER_STAT_ROWS = [
  {
    label: 'Average Length of Relationship',
    recentKey: 'lor_recent',
    lifetimeKey: 'lor_lifetime',
    recentPlaceholder: 'NA',
    lifetimePlaceholder: 'Months'
  },
  {
    label: 'Average Payment Completion',
    recentKey: 'pcr_recent',
    lifetimeKey: 'pcr_lifetime',
    recentPlaceholder: '%',
    lifetimePlaceholder: '%'
  },
  {
    label: 'Average <=60 Days Payment Completion',
    recentKey: 'six_dpr_recent',
    lifetimeKey: 'six_dpr_lifetime',
    recentPlaceholder: '%',
    lifetimePlaceholder: '%'
  },
  {
    label: 'Weighted ADTP (from due date)',
    recentKey: 'adtp_recent',
    lifetimeKey: 'adtp_lifetime',
    recentPlaceholder: 'Days',
    lifetimePlaceholder: 'Days'
  }
];

const EMPTY_PAYER_STATS = {
  lor_recent: '',
  lor_lifetime: '',
  pcr_recent: '',
  pcr_lifetime: '',
  six_dpr_recent: '',
  six_dpr_lifetime: '',
  adtp_recent: '',
  adtp_lifetime: ''
};

const EMPTY_INDUSTRY_ROW = {
  industryType: '',
  arOutstanding: '',
  salesContribution: ''
};

export default {
  name: 'PayerReportDetails',
  components: {
    BaseButton,
    PageHeader,
    AnalysisCard,
    PayerScoreReportPreview,
    ReportTableEditor
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      candidate: null,
      candidateName: 'Candidate',
      reportContext: null,
      merchantName: '',
      companyRequirement: '',
      keyAuthorisedSignatory: '',
      primePayersOverview: [],
      primePayerStats: { ...EMPTY_PAYER_STATS, lor_recent: 'NA' },
      primePayerPcrFootnote: '',
      primePayerSectorStats: [],
      approvedPayersOverview: [],
      approvedPayerStats: { ...EMPTY_PAYER_STATS, lor_recent: 'NA' },
      approvedPayerPcrFootnote: '',
      approvedPayerSectorStats: [],
      primePayersOutstandingStats: [],
      primePayerOutstandingTableColumns: PRIME_PAYER_OUTSTANDING_TABLE_COLUMNS,
      emptyPrimePayerOutstandingRow: { ...EMPTY_PRIME_PAYER_OUTSTANDING_ROW },
      primePayersSalesStats: [],
      primePayerSalesTableColumns: PRIME_PAYER_SALES_TABLE_COLUMNS,
      emptyPrimePayerSalesRow: { ...EMPTY_PRIME_PAYER_SALES_ROW },
      primePayersAgingStats: [],
      primePayerAgingTableColumns: PRIME_PAYER_AGING_TABLE_COLUMNS,
      emptyPrimePayerAgingRow: { ...EMPTY_PRIME_PAYER_AGING_ROW },
      approvedPayersOutstandingStats: [],
      approvedPayerOutstandingTableColumns: APPROVED_PAYER_OUTSTANDING_TABLE_COLUMNS,
      emptyApprovedPayerOutstandingRow: { ...EMPTY_APPROVED_PAYER_OUTSTANDING_ROW },
      approvedPayersSalesStats: [],
      approvedPayerSalesTableColumns: APPROVED_PAYER_SALES_TABLE_COLUMNS,
      emptyApprovedPayerSalesRow: { ...EMPTY_APPROVED_PAYER_SALES_ROW },
      approvedPayersAgingStats: [],
      approvedPayerAgingTableColumns: APPROVED_PAYER_AGING_TABLE_COLUMNS,
      emptyApprovedPayerAgingRow: { ...EMPTY_APPROVED_PAYER_AGING_ROW },
      conclusionPoints: [],
      recommendationPoints: [],
      payerStatRows: PAYER_STAT_ROWS,
      currentPreviewPage: 1
    };
  },
  computed: {
    analysisData() {
      return this.reportContext?.analysis_data
        || this.reportContext?.analysisData
        || null;
    },

    totalPreviewPages() {
      return PAYER_REPORT_PREVIEW_PAGE_COUNT;
    },

    currentPreviewPageTitle() {
      const match = PAYER_REPORT_PREVIEW_PAGES.find(
        (entry) => entry.page === this.currentPreviewPage
      );

      return match?.title || '';
    },

    usesFullWidthEditor() {
      return PAYER_REPORT_FULL_WIDTH_TABLE_PAGES.includes(this.currentPreviewPage);
    },

    reportPreviewData() {
      const resolvedName = this.merchantName?.trim()
        || this.getReportContextValue('merchant_name', 'merchantName')
        || this.candidate?.name
        || this.candidate?.company
        || this.candidateName
        || 'Merchant Name';

      return {
        merchantName: resolvedName,
        preparedBy: this.getReportContextValue('prepared_by', 'preparedBy') || '—',
        reportDate: this.formatContextDate(
          this.getReportContextValue('assessment_date', 'report_date', 'date')
        ) || this.formatDisplayDate(new Date()),
        establishmentDate: this.formatContextDate(
          this.getReportContextValue(
            'establishment_date',
            'establishmentDate',
            'business_incorporation_date'
          )
        ),
        industry: this.getReportContextValue(
          'industry',
          'main_sector_type',
          'mainSectorType',
          'subsector',
          'main_sector'
        ) || '—',
        keyShareholder: this.keyAuthorisedSignatory?.trim()
          || this.getReportContextValue(
            'key_authorised_signatory',
            'key_shareholder',
            'keyShareholder'
          )
          || '—',
        companyOverview: this.companyRequirement?.trim() || '—',
        facilityRequested: this.getReportContextValue(
          'facility_requested',
          'facilityRequested'
        ) || 'Invoice Financing',
        facilityLimit: this.formatContextCurrency(
          this.getReportContextValue('facility_limit', 'facilityLimit')
        ),
        tenorRequested: this.getReportContextValue(
          'facility_tenor',
          'tenor_requested',
          'tenorRequested'
        ) || '—',
        primePayersOverview: this.primePayersOverview,
        primePayerStats: this.primePayerStats,
        primePayerPcrFootnote: this.primePayerPcrFootnote?.trim() || '',
        primePayerSectorStats: this.primePayerSectorStats,
        approvedPayersOverview: this.approvedPayersOverview,
        approvedPayerStats: this.approvedPayerStats,
        approvedPayerPcrFootnote: this.approvedPayerPcrFootnote?.trim() || '',
        approvedPayerSectorStats: this.approvedPayerSectorStats,
        primePayersOutstandingStats: this.primePayersOutstandingStats,
        primePayersSalesStats: this.primePayersSalesStats,
        primePayersAgingStats: this.primePayersAgingStats,
        approvedPayersOutstandingStats: this.approvedPayersOutstandingStats,
        approvedPayersSalesStats: this.approvedPayersSalesStats,
        approvedPayersAgingStats: this.approvedPayersAgingStats,
        conclusionPoints: this.conclusionPoints,
        recommendationPoints: this.recommendationPoints
      };
    }
  },
  watch: {
    currentPreviewPage(page) {
      if (page === 3 || page === 7) {
        this.resizeIndustryTypeTextareas();
      }
    }
  },
  async mounted() {
    await this.loadCandidate();
    await this.loadPayerReportContext();
    this.initializeMerchantName();
    this.initializeReportInputDefaults();
    this.initializePage3Fields();
    this.initializePage4Fields();
    this.initializePage5Fields();
    this.initializePage6Fields();
    this.initializePage7Fields();
    this.initializePage8Fields();
    this.initializePage9Fields();
    this.initializePage10Fields();
    this.initializePage11Fields();
  },
  methods: {
    goToAutomatedAssessment() {
      this.$router.push({
        name: 'chatbot-automated-assessment-input',
        params: { candidateId: this.candidateId }
      });
    },

    async loadCandidate() {
      try {
        this.candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.candidateName = this.candidate?.name
          || this.candidate?.company
          || 'Candidate';
      } catch (err) {
        console.error('Error loading candidate:', err);
      }
    },

    async loadPayerReportContext() {
      this.loading = true;
      this.error = null;

      try {
        this.reportContext = await ChatbotService.getPayerScoreReportContext(this.candidateId);
      } catch (err) {
        console.error('Error loading payer report context:', err);
        this.error = 'Failed to load payer report context. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    initializeMerchantName() {
      if (this.merchantName?.trim()) return;

      const fromContext = this.getReportContextValue('merchant_name', 'merchantName');
      if (fromContext) {
        this.merchantName = String(fromContext);
        return;
      }

      const fallback = this.candidate?.name
        || this.candidate?.company
        || this.candidateName;

      if (fallback) {
        this.merchantName = String(fallback);
      }
    },

    initializeReportInputDefaults() {
      if (!this.companyRequirement) {
        const requirement = this.getReportContextValue(
          'company_requirement',
          'companyRequirement',
          'requirement',
          'company_overview'
        );
        if (requirement) {
          this.companyRequirement = String(requirement);
        }
      }

      if (!this.keyAuthorisedSignatory) {
        const signatory = this.getReportContextValue(
          'key_authorised_signatory',
          'key_shareholder',
          'keyShareholder'
        );
        if (signatory) {
          this.keyAuthorisedSignatory = String(signatory);
        }
      }
    },

    initializePage3Fields() {
      const overview = this.resolveAnalysisField('prime_payer_analysis');
      if (overview.length) {
        this.primePayersOverview = [...overview];
      }

      this.primePayerStats = {
        ...this.mapPrimePayerStatsFromContext(this.reportContext?.prime_payer_stats),
        ...(this.analysisData?.prime_payer_stats && typeof this.analysisData.prime_payer_stats === 'object'
          ? this.analysisData.prime_payer_stats
          : {})
      };

      const footnote = this.analysisData?.prime_payer_pcr_footnote
        || this.getReportContextValue('prime_payer_pcr_footnote');
      if (footnote) {
        this.primePayerPcrFootnote = String(footnote);
      }

      const sectorRows = this.resolveSavedOrContextArray('prime_payer_sector_stats');

      if (sectorRows.length) {
        this.primePayerSectorStats = sectorRows.map((row) => this.normalizeIndustryRow(row));
      } else if (!this.primePayerSectorStats.length) {
        this.primePayerSectorStats = [{ ...EMPTY_INDUSTRY_ROW }];
      }
    },

    initializePage4Fields() {
      const saved = this.analysisData?.prime_payers_oustanding_stats
        || this.analysisData?.prime_payers_outstanding_stats;
      const fromContext = this.reportContext?.prime_payers_oustanding_stats
        || this.reportContext?.prime_payers_outstanding_stats
        || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.primePayersOutstandingStats = source.map(
          (row) => this.normalizePrimePayerOutstandingRow(row)
        );
      } else if (!this.primePayersOutstandingStats.length) {
        this.primePayersOutstandingStats = [{ ...EMPTY_PRIME_PAYER_OUTSTANDING_ROW }];
      }
    },

    initializePage5Fields() {
      const saved = this.analysisData?.prime_payers_sales_stats;
      const fromContext = this.reportContext?.prime_payers_sales_stats || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.primePayersSalesStats = source.map(
          (row) => this.normalizePrimePayerSalesRow(row)
        );
      } else if (!this.primePayersSalesStats.length) {
        this.primePayersSalesStats = [{ ...EMPTY_PRIME_PAYER_SALES_ROW }];
      }
    },

    initializePage6Fields() {
      const saved = this.analysisData?.prime_payers_aging_stats;
      const fromContext = this.reportContext?.prime_payers_aging_stats || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.primePayersAgingStats = source.map(
          (row) => this.normalizePrimePayerAgingRow(row)
        );
      } else if (!this.primePayersAgingStats.length) {
        this.primePayersAgingStats = [{ ...EMPTY_PRIME_PAYER_AGING_ROW }];
      }
    },

    initializePage7Fields() {
      const overview = this.resolveAnalysisField('approved_payer_analysis');
      if (overview.length) {
        this.approvedPayersOverview = [...overview];
      }

      this.approvedPayerStats = {
        ...this.mapPrimePayerStatsFromContext(this.reportContext?.approved_payer_stats),
        ...(this.analysisData?.approved_payer_stats
          && typeof this.analysisData.approved_payer_stats === 'object'
          ? this.analysisData.approved_payer_stats
          : this.analysisData?.kema_approved_payer_stats
            && typeof this.analysisData.kema_approved_payer_stats === 'object'
            ? this.analysisData.kema_approved_payer_stats
            : {})
      };

      const footnote = this.analysisData?.approved_payer_pcr_footnote
        || this.getReportContextValue('approved_payer_pcr_footnote');
      if (footnote) {
        this.approvedPayerPcrFootnote = String(footnote);
      }

      const sectorRows = this.resolveSavedOrContextArray('approved_payer_sector_stats');

      if (sectorRows.length) {
        this.approvedPayerSectorStats = sectorRows.map((row) => this.normalizeIndustryRow(row));
      } else if (!this.approvedPayerSectorStats.length) {
        this.approvedPayerSectorStats = [{ ...EMPTY_INDUSTRY_ROW }];
      }
    },

    initializePage8Fields() {
      const saved = this.analysisData?.approved_payers_oustanding_stats
        || this.analysisData?.approved_payers_outstanding_stats;
      const fromContext = this.reportContext?.approved_payers_oustanding_stats
        || this.reportContext?.approved_payers_outstanding_stats
        || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.approvedPayersOutstandingStats = source.map(
          (row) => this.normalizeApprovedPayerOutstandingRow(row)
        );
      } else if (!this.approvedPayersOutstandingStats.length) {
        this.approvedPayersOutstandingStats = [{ ...EMPTY_APPROVED_PAYER_OUTSTANDING_ROW }];
      }
    },

    initializePage9Fields() {
      const saved = this.analysisData?.approved_payers_sales_stats;
      const fromContext = this.reportContext?.approved_payers_sales_stats || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.approvedPayersSalesStats = source.map(
          (row) => this.normalizeApprovedPayerSalesRow(row)
        );
      } else if (!this.approvedPayersSalesStats.length) {
        this.approvedPayersSalesStats = [{ ...EMPTY_APPROVED_PAYER_SALES_ROW }];
      }
    },

    initializePage10Fields() {
      const saved = this.analysisData?.approved_payers_aging_stats;
      const fromContext = this.reportContext?.approved_payers_aging_stats || [];

      const source = Array.isArray(saved) && saved.length
        ? saved
        : fromContext;

      if (Array.isArray(source) && source.length) {
        this.approvedPayersAgingStats = source.map(
          (row) => this.normalizeApprovedPayerAgingRow(row)
        );
      } else if (!this.approvedPayersAgingStats.length) {
        this.approvedPayersAgingStats = [{ ...EMPTY_APPROVED_PAYER_AGING_ROW }];
      }
    },

    initializePage11Fields() {
      const conclusions = this.resolveAnalysisField(
        'conclusion_points',
        'payer_conclusion_points'
      );
      const recommendations = this.resolveAnalysisField(
        'recommendation_points',
        'payer_recommendation_points'
      );

      if (conclusions.length) {
        this.conclusionPoints = [...conclusions];
      }

      if (recommendations.length) {
        this.recommendationPoints = [...recommendations];
      }
    },

    resolveSavedOrContextArray(field, ...contextKeys) {
      const saved = this.analysisData?.[field];
      if (Array.isArray(saved) && saved.length) {
        return saved;
      }

      const context = this.reportContext || {};
      const keys = contextKeys.length ? contextKeys : [field];

      for (let index = 0; index < keys.length; index += 1) {
        const value = context[keys[index]];
        if (Array.isArray(value) && value.length) {
          return value;
        }
      }

      return [];
    },

    resolveAnalysisField(field, ...contextKeys) {
      const analysis = this.analysisData || {};
      const context = this.reportContext || {};

      if (Array.isArray(analysis[field]) && analysis[field].length) {
        return analysis[field];
      }

      for (let index = 0; index < contextKeys.length; index += 1) {
        const key = contextKeys[index];
        const value = context[key];
        if (Array.isArray(value) && value.length) {
          return value;
        }
      }

      if (Array.isArray(context[field]) && context[field].length) {
        return context[field];
      }

      return [];
    },

    mapPrimePayerStatsFromContext(stats) {
      if (!stats || typeof stats !== 'object') {
        return {
          ...EMPTY_PAYER_STATS,
          lor_recent: 'NA'
        };
      }

      return {
        lor_recent: 'NA',
        lor_lifetime: this.formatStatInput(stats.lor_sum),
        pcr_recent: this.formatStatInput(stats.pcr_sum),
        pcr_lifetime: this.formatStatInput(stats.pcr_lifetime_sum),
        six_dpr_recent: this.formatStatInput(stats.six_dpr_sum),
        six_dpr_lifetime: this.formatStatInput(stats.six_dpr_lifetime_sum),
        adtp_recent: this.formatStatInput(stats.adtp_sum),
        adtp_lifetime: this.formatStatInput(stats.adtp_lifetime_sum)
      };
    },

    formatStatInput(value) {
      if (value === undefined || value === null || value === '') return '';
      return String(value);
    },

    normalizeIndustryRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_INDUSTRY_ROW };
      }

      return {
        industryType: row.sector
          ?? row.industry_type
          ?? row.industryType
          ?? row.label
          ?? row.industry
          ?? '',
        arOutstanding: row.ar_outstanding_aed
          ?? row.ar_outstanding
          ?? row.arOutstanding
          ?? row.ar
          ?? '',
        salesContribution: row.sales_contrib_aed
          ?? row.sales_contribution
          ?? row.salesContribution
          ?? row.sales
          ?? ''
      };
    },

    normalizePrimePayerOutstandingRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_PRIME_PAYER_OUTSTANDING_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        payer_sector: row.payer_sector ?? row.payerSector ?? '',
        oustanding_aed: this.formatStatInput(
          row.oustanding_aed ?? row.outstanding_aed ?? row.arOutstanding
        ),
        oustanding_pct: this.formatStatInput(
          row.oustanding_pct ?? row.outstanding_pct ?? row.arPercent
        ),
        weighted_adtp: this.formatStatInput(
          row.weighted_adtp ?? row.adtp
        )
      };
    },

    normalizeApprovedPayerOutstandingRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_APPROVED_PAYER_OUTSTANDING_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        payer_sector: row.payer_sector ?? row.payerSector ?? '',
        oustanding_aed: this.formatStatInput(
          row.oustanding_aed ?? row.outstanding_aed ?? row.arOutstanding
        ),
        oustanding_pct: this.formatStatInput(
          row.oustanding_pct ?? row.outstanding_pct ?? row.arPercent
        ),
        weighted_adtp: this.formatStatInput(
          row.weighted_adtp ?? row.adtp
        )
      };
    },

    normalizePrimePayerSalesRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_PRIME_PAYER_SALES_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        payer_sector: row.payer_sector ?? row.payerSector ?? '',
        sales_aed: this.formatStatInput(row.sales_aed ?? row.salesAed),
        sales_pct: this.formatStatInput(row.sales_pct ?? row.salesPct),
        parent: row.parent ?? row.associated_parent ?? row.associatedParent ?? ''
      };
    },

    normalizeApprovedPayerSalesRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_APPROVED_PAYER_SALES_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        payer_sector: row.payer_sector ?? row.payerSector ?? '',
        sales_aed: this.formatStatInput(row.sales_aed ?? row.salesAed),
        sales_pct: this.formatStatInput(row.sales_pct ?? row.salesPct),
        parent: row.parent ?? row.associated_parent ?? row.associatedParent ?? ''
      };
    },

    normalizePrimePayerAgingRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_PRIME_PAYER_AGING_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        '0_30_days': this.formatStatInput(row['0_30_days'] ?? row.zeroTo30Days),
        '31_60_days': this.formatStatInput(row['31_60_days'] ?? row.thirtyOneTo60Days),
        '61_90_days': this.formatStatInput(row['61_90_days'] ?? row.sixtyOneTo90Days),
        '>90_days': this.formatStatInput(row['>90_days'] ?? row.over90Days),
        pcr: this.formatStatInput(row.pcr)
      };
    },

    normalizeApprovedPayerAgingRow(row) {
      if (!row || typeof row !== 'object') {
        return { ...EMPTY_APPROVED_PAYER_AGING_ROW };
      }

      return {
        payer_name: row.payer_name ?? row.payerName ?? '',
        '0_30_days': this.formatStatInput(row['0_30_days'] ?? row.zeroTo30Days),
        '31_60_days': this.formatStatInput(row['31_60_days'] ?? row.thirtyOneTo60Days),
        '61_90_days': this.formatStatInput(row['61_90_days'] ?? row.sixtyOneTo90Days),
        '>90_days': this.formatStatInput(row['>90_days'] ?? row.over90Days),
        pcr: this.formatStatInput(row.pcr)
      };
    },

    updatePrimePayersOverview(items) {
      this.primePayersOverview = Array.isArray(items) ? items : [];
    },

    updateApprovedPayersOverview(items) {
      this.approvedPayersOverview = Array.isArray(items) ? items : [];
    },

    updateConclusionPoints(items) {
      this.conclusionPoints = Array.isArray(items) ? items : [];
    },

    updateRecommendationPoints(items) {
      this.recommendationPoints = Array.isArray(items) ? items : [];
    },

    getPayerStatValue(key) {
      return this.primePayerStats?.[key] ?? '';
    },

    updatePayerStat(key, value) {
      this.primePayerStats = {
        ...this.primePayerStats,
        [key]: value
      };
    },

    getApprovedPayerStatValue(key) {
      return this.approvedPayerStats?.[key] ?? '';
    },

    updateApprovedPayerStat(key, value) {
      this.approvedPayerStats = {
        ...this.approvedPayerStats,
        [key]: value
      };
    },

    addApprovedPayerSectorRow() {
      this.approvedPayerSectorStats = [
        ...this.approvedPayerSectorStats,
        { ...EMPTY_INDUSTRY_ROW }
      ];
      this.$nextTick(() => this.resizeIndustryTypeTextareas());
    },

    addPrimePayerSectorRow() {
      this.primePayerSectorStats = [
        ...this.primePayerSectorStats,
        { ...EMPTY_INDUSTRY_ROW }
      ];
      this.$nextTick(() => this.resizeIndustryTypeTextareas());
    },

    resizeIndustryTypeTextarea(textarea) {
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    },

    resizeIndustryTypeTextareas() {
      this.$nextTick(() => {
        if (!this.$el) return;
        const textareas = this.$el.querySelectorAll('.industry-type-textarea');
        textareas.forEach((textarea) => this.resizeIndustryTypeTextarea(textarea));
      });
    },

    onIndustryTypeInput(event) {
      this.resizeIndustryTypeTextarea(event.target);
    },

    formatContextDate(value) {
      if (!value) return '—';
      if (typeof value === 'string' && /[A-Za-z]{3}\s+\d{1,2},\s+\d{4}/.test(value)) {
        return value;
      }
      return this.formatDisplayDate(value);
    },

    formatContextCurrency(value) {
      if (value === undefined || value === null || value === '') return '—';
      if (typeof value === 'string' && value.trim().toUpperCase().startsWith('AED')) {
        return value;
      }
      return this.formatReportCurrency(value);
    },

    formatDisplayDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatAmount(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    formatReportCurrency(value) {
      if (value === undefined || value === null || value === '') return '—';
      const formatted = this.formatAmount(value);
      if (formatted === '—') return formatted;
      return `AED ${formatted}`;
    },

    getReportContextValue(...keys) {
      const context = this.reportContext || {};

      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        const value = context[key];

        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }

      return null;
    },

    goToPrevPreviewPage(event) {
      if (event && this.isEditableElement(event.target)) return;
      if (event) event.preventDefault();

      if (this.currentPreviewPage <= 1) return;
      this.currentPreviewPage -= 1;
    },

    goToNextPreviewPage(event) {
      if (event && this.isEditableElement(event.target)) return;
      if (event) event.preventDefault();

      if (this.currentPreviewPage >= this.totalPreviewPages) return;
      this.currentPreviewPage += 1;
    },

    isEditableElement(element) {
      if (!element || !element.tagName) return false;

      const tag = element.tagName.toLowerCase();

      return tag === 'input'
        || tag === 'textarea'
        || tag === 'select'
        || element.isContentEditable;
    },

    formatDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '—';
      }

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

.payer-report-details-page {
  background: #ffffff;
  min-height: 100vh;
}

.main-content {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid $color-primary;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    font-size: 1.1rem;
    margin: 0;
  }
}

.error-state {
  .error-icon {
    font-size: 3rem;
    color: #e53e3e;
    margin-bottom: 1rem;
  }

  h3 {
    color: #2d3748;
    margin: 0 0 1rem;
    font-size: 1.5rem;
  }

  .error-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }

  .retry-btn,
  .back-btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .retry-btn {
    background: $color-primary;
    color: #ffffff;
  }

  .back-btn {
    background: #edf2f7;
    color: #2d3748;
  }
}

.report-back-action {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
}

.report-overview-card {
  background: #ffffff;
  margin-bottom: 1.5rem;
}

.report-header {
  display: flex;
  align-items: center;
  gap: 1rem;

  i {
    font-size: 2rem;
    color: $color-primary;
  }
}

.report-title {
  margin: 0 0 0.35rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  font-family: $brand-font-medium;
}

.report-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: #718096;
  font-size: 0.9rem;
}

.report-editor-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.report-full-width-inputs {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.report-editor-layout {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) 816px;
  gap: 2rem;
  align-items: start;

  &.is-full-width-stack {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.report-full-width-preview {
  width: 100%;
  align-items: center;

  .report-page-navigator,
  .report-preview-panel {
    width: 100%;
    max-width: 816px;
  }
}

.report-inputs-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.panel-title {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.panel-page-label {
  margin: -0.25rem 0 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.report-page-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.report-inputs-empty {
  margin: 0.5rem 0 0;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
}

.report-preview-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.report-page-navigator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  position: sticky;
  top: 1.5rem;
  z-index: 2;
}

.report-page-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  i {
    font-size: 1.25rem;
    color: #94a3b8;
    transition: color 0.15s ease;
  }

  &:hover:not(:disabled) i {
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.report-page-nav-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  min-width: 0;
  text-align: center;
}

.report-page-nav-count {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.report-page-nav-title {
  font-size: 0.78rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
}

.report-preview-panel {
  min-width: 0;
}

.form-section {
  margin-bottom: 1rem;
}

.section-title {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.field-hint {
  margin: -0.25rem 0 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.field-hint-inline {
  margin-top: -0.15rem;
}

.payer-stats-form-section {
  margin-top: 0;
}

.payer-stats-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 1px solid #111827;
  border-left: 1px solid #111827;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border: none;
    border-right: 1px solid #111827;
    border-bottom: 1px solid #111827;
    padding: 6px 8px;
    vertical-align: middle;
    text-align: left;
  }

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
  }

  tbody td.label-cell {
    font-weight: 700;
    width: 46%;
  }

  tbody td:nth-child(2),
  tbody td:nth-child(3) {
    width: 27%;
  }
}

.industry-input-table tbody td {
  width: auto;
}

.payer-stat-cell-input {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  font-family: $brand-font-regular;
  color: #2d3748;
  background: #ffffff;
  transition: border-color 0.15s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.industry-type-textarea {
  display: block;
  min-height: 30px;
  line-height: 1.4;
  resize: none;
  overflow: hidden;
}

.industry-input-table tbody td:first-child {
  min-width: 220px;
}

.add-industry-row-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $color-primary;
    color: $color-primary;
    background: #ffffff;
  }
}

.text-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
}

.textarea-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1320px) {
  .report-editor-layout {
    grid-template-columns: 1fr;
  }

  .report-editor-layout.is-preview-only {
    justify-items: stretch;
  }

  .report-page-navigator {
    position: static;
  }
}
</style>
